import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, RefreshCw, RotateCcw, FileText, Tag, Filter, Building2, ChevronDown, ChevronUp, Zap, AlertTriangle, CheckCircle2 } from 'lucide-react';
import FiltroData from './FiltroData';
import FiltrosCategoria from './FiltrosCategoria';
import SearchableSelect from 'components/common/SearchableSelect';

const FiltrosOtimizados = ({
  // Props do período
  range,
  setRange,
  loading,
  
  // Props da busca principal
  busca,
  setBusca,
  
  // Props das categorias
  contasFonte,
  filtroPlanejamento,
  setFiltroPlanejamento,
  filtroSubcategoria,
  setFiltroSubcategoria,
  
  // Props do tipo de pagamento
  filtroTipoPagamento,
  setFiltroTipoPagamento,
  
  // Props dos arquivos
  filtroArquivosAtual,
  setFiltroArquivosAtual,
  filtroTipoDocumento,
  setFiltroTipoDocumento,
  buscaDocumento,
  setBuscaDocumento,
  negacaoFiltro,
  setNegacaoFiltro,
  
	  // Props de conta bancária
	  filtroContaBancaria,
	  setFiltroContaBancaria,
	  
	  // Props de unidade (quando existir no dataset)
	  filtroUnidade,
	  setFiltroUnidade,
	  
	  // Props de ações
	  onRefresh,
	  onClearAll,
  contasFiltradas = [],
  totalContas = 0,
  
  // Props dos filtros rápidos
  filtroAtivo,
  setFiltroAtivo,
  contasPagas = [],
  contasPagar = [],
  contasProntasParaPagar = []
}) => {
  const debounceBuscaRef = useRef(null);
  const debounceBuscaDocumentoRef = useRef(null);
  const [buscaLocal, setBuscaLocal] = useState(busca);
  const [buscaDocumentoLocal, setBuscaDocumentoLocal] = useState(buscaDocumento);

  // Estado para controlar expansão de filtros em mobile
  const [filtrosExpandidos, setFiltrosExpandidos] = useState({
    principais: true,
    arquivos: false
  });

  // Estado para controlar se os filtros estão minimizados
  const [filtrosMinimizados, setFiltrosMinimizados] = useState(() => {
    // Carregar preferência do localStorage
    try {
      const saved = localStorage.getItem('filtros_contas_pagar_minimizados');
      return saved ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  // Salvar preferência no localStorage quando mudar
  useEffect(() => {
    try {
      localStorage.setItem('filtros_contas_pagar_minimizados', JSON.stringify(filtrosMinimizados));
    } catch (error) {
      console.warn('Erro ao salvar preferência de filtros:', error);
    }
  }, [filtrosMinimizados]);

  useEffect(() => {
    setBuscaLocal(busca);
  }, [busca]);

  useEffect(() => {
    setBuscaDocumentoLocal(buscaDocumento);
  }, [buscaDocumento]);

  useEffect(() => {
    if (debounceBuscaRef.current) {
      clearTimeout(debounceBuscaRef.current);
    }
    debounceBuscaRef.current = setTimeout(() => {
      setBusca(buscaLocal);
    }, 300);

    return () => {
      if (debounceBuscaRef.current) {
        clearTimeout(debounceBuscaRef.current);
      }
    };
  }, [buscaLocal, setBusca]);

  useEffect(() => {
    if (debounceBuscaDocumentoRef.current) {
      clearTimeout(debounceBuscaDocumentoRef.current);
    }
    debounceBuscaDocumentoRef.current = setTimeout(() => {
      setBuscaDocumento(buscaDocumentoLocal);
    }, 300);

    return () => {
      if (debounceBuscaDocumentoRef.current) {
        clearTimeout(debounceBuscaDocumentoRef.current);
      }
    };
  }, [buscaDocumentoLocal, setBuscaDocumento]);

  // Extrair contas bancárias únicas
	  const contasBancariasUnicas = useMemo(() => {
	    const contas = new Set();
	    contasFonte.forEach(c => {
	      const conta = c.conta || 'Não informado';
	      if (conta) contas.add(conta);
	    });
	    return Array.from(contas).sort();
	  }, [contasFonte]);

	  const unidadesUnicas = useMemo(() => {
	    const unidades = new Set();
	    contasFonte.forEach(c => {
	      const unidade = c.unidade || null;
	      if (unidade) unidades.add(unidade);
	    });
	    return Array.from(unidades).sort();
	  }, [contasFonte]);

	  useEffect(() => {
	    if (filtroUnidade && !unidadesUnicas.includes(filtroUnidade)) {
	      setFiltroUnidade(null);
	    }
	  }, [filtroUnidade, unidadesUnicas, setFiltroUnidade]);

  // Filtros rápidos
  const filtrosRapidos = useMemo(() => [
    {
      id: 'todas',
      label: 'Todas',
      icon: null,
      color: 'gray',
      count: totalContas
    },
    {
      id: 'vencidas',
      label: 'Vencidas',
      icon: AlertTriangle,
      color: 'red',
      count: contasPagar.filter(c => {
        const v = new Date(c.data_vencimento_convertida || c.dataVencimento);
        const hoje = new Date();
        hoje.setHours(0,0,0,0);
        return v < hoje;
      }).length
    },
    {
      id: 'prontas_para_pagar',
      label: 'Prontas para Pagar',
      icon: CheckCircle2,
      color: 'blue',
      count: contasProntasParaPagar.length
    },
    {
      id: 'pagas',
      label: 'Pagas',
      icon: null,
      color: 'green',
      count: contasPagas.length
    }
  ], [totalContas, contasPagar, contasProntasParaPagar, contasPagas]);

  useEffect(() => {
    const filtrosDisponiveis = new Set(filtrosRapidos.map((filtro) => filtro.id));
    if (!filtrosDisponiveis.has(filtroAtivo)) {
      setFiltroAtivo('todas');
    }
  }, [filtroAtivo, filtrosRapidos, setFiltroAtivo]);
  const filtroRapidoAtivo = filtrosRapidos.find((filtro) => filtro.id === filtroAtivo) || filtrosRapidos[0];

  const getColorClassesRapidos = (color, isActive = false) => {
    const baseClasses = "px-3 py-2 rounded-[var(--cp-radius-pill,999px)] text-sm font-semibold transition-all duration-[var(--cp-motion-fast,180ms)] flex items-center gap-2 border motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))]";
    if (isActive) {
      return `${baseClasses} border-transparent bg-[color:var(--accent)] text-white [box-shadow:var(--cp-shadow-glow,none)]`;
    }
    return `${baseClasses} border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] text-[color:var(--text-primary)] hover:border-[color:var(--cp-border-strong,var(--border))] hover:bg-[color:var(--cp-surface,var(--surface))]`;
  };

  const filtrosAtivosCount = useMemo(
    () =>
      [
        busca ? 1 : 0,
        filtroContaBancaria ? 1 : 0,
        filtroUnidade ? 1 : 0,
        filtroPlanejamento ? 1 : 0,
        filtroSubcategoria ? 1 : 0,
        filtroTipoPagamento !== 'todos' ? 1 : 0,
        filtroArquivosAtual !== 'todas' ? 1 : 0,
        filtroTipoDocumento !== 'todos' ? 1 : 0,
        buscaDocumento ? 1 : 0,
        range.presetId !== 'mes_atual' ? 1 : 0
      ].reduce((a, b) => a + b, 0),
    [
      busca,
      filtroContaBancaria,
      filtroUnidade,
      filtroPlanejamento,
      filtroSubcategoria,
      filtroTipoPagamento,
      filtroArquivosAtual,
      filtroTipoDocumento,
      buscaDocumento,
      range.presetId
    ]
  );
  const filtrosAvancadosAtivosCount = useMemo(
    () =>
      [
        filtroContaBancaria ? 1 : 0,
        filtroUnidade ? 1 : 0,
        filtroPlanejamento ? 1 : 0,
        filtroSubcategoria ? 1 : 0,
        filtroTipoPagamento !== 'todos' ? 1 : 0,
        filtroArquivosAtual !== 'todas' ? 1 : 0,
        filtroTipoDocumento !== 'todos' ? 1 : 0,
        buscaDocumento ? 1 : 0
      ].reduce((a, b) => a + b, 0),
    [
      filtroContaBancaria,
      filtroUnidade,
      filtroPlanejamento,
      filtroSubcategoria,
      filtroTipoPagamento,
      filtroArquivosAtual,
      filtroTipoDocumento,
      buscaDocumento
    ]
  );
  const filtrosPrincipaisAtivosCount = useMemo(
    () =>
      [
        filtroContaBancaria ? 1 : 0,
        filtroUnidade ? 1 : 0,
        filtroPlanejamento ? 1 : 0,
        filtroSubcategoria ? 1 : 0,
        filtroTipoPagamento !== 'todos' ? 1 : 0
      ].reduce((a, b) => a + b, 0),
    [
      filtroContaBancaria,
      filtroUnidade,
      filtroPlanejamento,
      filtroSubcategoria,
      filtroTipoPagamento
    ]
  );
  const filtrosArquivosAtivosCount = useMemo(
    () =>
      [
        filtroArquivosAtual !== 'todas' ? 1 : 0,
        filtroTipoDocumento !== 'todos' ? 1 : 0,
        buscaDocumento ? 1 : 0
      ].reduce((a, b) => a + b, 0),
    [filtroArquivosAtual, filtroTipoDocumento, buscaDocumento]
  );
  const filtrosAtivosResumo = useMemo(() => {
    const items = [];
    if (busca) {
      items.push({ key: 'busca', label: `Busca: ${busca}` });
    }
    if (range.presetId !== 'mes_atual') {
      items.push({ key: 'periodo', label: `Período: ${range.presetId.replaceAll('_', ' ')}` });
    }
    if (filtroUnidade) {
      items.push({ key: 'unidade', label: `Unidade: ${filtroUnidade}` });
    }
    if (filtroContaBancaria) {
      items.push({ key: 'conta', label: `Conta: ${filtroContaBancaria}` });
    }
    if (filtroPlanejamento) {
      items.push({ key: 'planejamento', label: `Categoria: ${filtroPlanejamento}` });
    }
    if (filtroSubcategoria) {
      items.push({ key: 'subcategoria', label: `Subcategoria: ${filtroSubcategoria}` });
    }
    if (filtroTipoPagamento !== 'todos') {
      items.push({ key: 'tipo_pagamento', label: `Pagamento: ${filtroTipoPagamento}` });
    }
    if (filtroArquivosAtual !== 'todas') {
      items.push({ key: 'arquivos', label: `Arquivos: ${filtroArquivosAtual.replaceAll('_', ' ')}` });
    }
    if (filtroTipoDocumento !== 'todos') {
      const prefixo = negacaoFiltro ? 'Excluir doc:' : 'Doc:';
      items.push({ key: 'tipo_documento', label: `${prefixo} ${filtroTipoDocumento.replaceAll('_', ' ')}` });
    }
    if (buscaDocumento) {
      items.push({ key: 'busca_documento', label: `Documento: ${buscaDocumento}` });
    }
    return items;
  }, [
    busca,
    range.presetId,
    filtroUnidade,
    filtroContaBancaria,
    filtroPlanejamento,
    filtroSubcategoria,
    filtroTipoPagamento,
    filtroArquivosAtual,
    filtroTipoDocumento,
    buscaDocumento,
    negacaoFiltro
  ]);

	  const temFiltrosAtivos = () => {
	    return filtrosAtivosCount > 0;
  };

  const resetRangeMesAtual = () => {
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const pad = (n) => String(n).padStart(2, '0');
    const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    setRange({ presetId: 'mes_atual', start: toISO(startOfMonth), end: toISO(endOfMonth) });
  };

  const removerFiltroAtivo = (key) => {
    switch (key) {
      case 'busca':
        setBuscaLocal('');
        setBusca('');
        break;
      case 'periodo':
        resetRangeMesAtual();
        break;
      case 'unidade':
        setFiltroUnidade(null);
        break;
      case 'conta':
        setFiltroContaBancaria(null);
        break;
      case 'planejamento':
        setFiltroPlanejamento(null);
        setFiltroSubcategoria(null);
        break;
      case 'subcategoria':
        setFiltroSubcategoria(null);
        break;
      case 'tipo_pagamento':
        setFiltroTipoPagamento('todos');
        break;
      case 'arquivos':
        setFiltroArquivosAtual('todas');
        break;
      case 'tipo_documento':
        setFiltroTipoDocumento('todos');
        setNegacaoFiltro(false);
        break;
      case 'busca_documento':
        setBuscaDocumentoLocal('');
        setBuscaDocumento('');
        break;
      default:
        break;
    }
  };

	  const limparTodosFiltros = () => {
	    setBusca('');
      setBuscaLocal('');
	    setFiltroContaBancaria(null);
	    setFiltroUnidade(null);
	    setFiltroPlanejamento(null);
	    setFiltroSubcategoria(null);
	    setFiltroTipoPagamento('todos');
    setFiltroArquivosAtual('todas');
    setFiltroTipoDocumento('todos');
    setBuscaDocumento('');
    setBuscaDocumentoLocal('');
    setNegacaoFiltro(false);
    resetRangeMesAtual();
    
    if (onClearAll) onClearAll();
  };

  const tiposDocumento = [
    { id: 'todos', label: 'Todos', color: 'blue' },
    { id: 'contratos', label: 'Contratos', color: 'purple' },
    { id: 'notas_fiscais', label: 'Notas Fiscais', color: 'yellow' },
    { id: 'comprovantes', label: 'Comprovantes', color: 'indigo' },
    { id: 'energia', label: 'Energia', color: 'red' },
    { id: 'boleto', label: 'Boleto', color: 'cyan' },
    { id: 'aluguel', label: 'Aluguel', color: 'emerald' },
    { id: 'outros', label: 'Outros', color: 'gray' }
  ];

  const getColorClasses = (color, isActive = false) => {
    const baseClasses = "px-3 py-1.5 rounded-[var(--cp-radius-pill,999px)] text-sm font-medium transition-all duration-[var(--cp-motion-fast,180ms)] flex items-center gap-2 border motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))]";
    if (isActive) {
      return `${baseClasses} bg-[color:var(--accent)] text-white border-transparent [box-shadow:var(--cp-shadow-glow,none)]`;
    }
    return `${baseClasses} bg-[color:var(--cp-surface-soft,var(--surface-muted))] text-[color:var(--text-primary)] border-[color:var(--cp-border,var(--border))] hover:border-[color:var(--cp-border-strong,var(--border))] hover:bg-[color:var(--cp-surface,var(--surface))]`;
  };

  return (
    <div className="mb-4 w-full max-w-full rounded-[var(--cp-radius-card,0.85rem)] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface,var(--surface))] [box-shadow:var(--cp-shadow-soft,none)]">
      {/* Filtros Rápidos integrados */}
      <div className="border-b border-[color:var(--cp-border,var(--border))] p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-[color:var(--accent)] flex-shrink-0" />
            <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Filtros Rápidos</h3>
          </div>
          <div className="flex items-center gap-2 text-xs text-[color:var(--text-muted)] whitespace-nowrap">
            <span>{contasFiltradas.length} de {totalContas} contas</span>
            {filtrosAtivosCount > 0 && (
              <span className="inline-flex items-center rounded-full border border-[color:var(--status-info-border,var(--border))] bg-[color:var(--status-info-bg,var(--surface-muted))] px-2 py-0.5 text-[11px] font-semibold text-[color:var(--status-info-fg,var(--text-primary))]">
                {filtrosAtivosCount} filtro{filtrosAtivosCount > 1 ? 's' : ''} ativo{filtrosAtivosCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>
        <div className="sr-only" aria-live="polite">
          {filtrosAtivosCount} filtro{filtrosAtivosCount === 1 ? '' : 's'} ativo{filtrosAtivosCount === 1 ? '' : 's'}
        </div>

        {/* Mobile: cards já controlam a visão ativa */}
        <div className="sm:hidden w-full">
          <div className="flex items-center justify-between gap-2 rounded-[0.7rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-3 py-2">
            <p className="text-sm text-[color:var(--text-primary)]">
              <span className="text-[color:var(--text-muted)]">Visão ativa:</span>{' '}
              <span className="font-semibold">
                {filtroRapidoAtivo?.label} ({filtroRapidoAtivo?.count ?? 0})
              </span>
            </p>
            {filtroAtivo !== 'todas' && (
              <button
                type="button"
                onClick={() => setFiltroAtivo('todas')}
                className="inline-flex min-h-[36px] items-center rounded-[0.65rem] border border-[color:var(--cp-border,var(--border))] px-2.5 py-1 text-xs font-semibold text-[color:var(--text-muted)] hover:bg-[color:var(--cp-surface,var(--surface))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))]"
              >
                Limpar visão
              </button>
            )}
          </div>
        </div>

        {/* Desktop: chips */}
        <div className="hidden sm:flex gap-2 flex-wrap w-full">
          {filtrosRapidos.map((filtro) => {
            const Icon = filtro.icon;
            const isActive = filtroAtivo === filtro.id;
            
            return (
              <button
                key={filtro.id}
                onClick={() => setFiltroAtivo(filtro.id)}
                className={`${getColorClassesRapidos(filtro.color, isActive)} flex-shrink-0`}
                aria-pressed={isActive}
              >
                {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
                <span className="whitespace-nowrap">{filtro.label}</span>
                <span
                  className={`px-2 py-0.5 rounded-[var(--cp-radius-pill,999px)] text-xs flex-shrink-0 ${
                    isActive
                      ? 'bg-white/90 text-[color:var(--accent)]'
                      : 'bg-[color:var(--cp-surface,var(--surface))] text-[color:var(--text-muted)]'
                  }`}
                >
                  {filtro.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Header compacto com busca e ações */}
      <div className="sticky top-0 z-30 border-b border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface,var(--surface))]/95 p-4 backdrop-blur-sm xl:static xl:bg-transparent xl:backdrop-blur-none">
        <div className="flex flex-col xl:flex-row xl:items-center gap-3 w-full">
          {/* Mobile: bloco unificado */}
          <div className="xl:hidden flex flex-col gap-2 w-full">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Buscar por fornecedor, descrição, categoria ou ID..."
                className="w-full min-h-[44px] rounded-[0.7rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-3 py-2 pr-9 text-sm transition-all duration-[var(--cp-motion-fast,180ms)] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))] focus-visible:border-[color:var(--accent)] hover:border-[color:var(--cp-border-strong,var(--border))]"
                value={buscaLocal}
                onChange={(e) => setBuscaLocal(e.target.value)}
                aria-label="Buscar contas a pagar"
              />
              <Search className="h-4 w-4 text-[color:var(--text-muted)] absolute right-3 top-1/2 -translate-y-1/2" />
            </div>

            <div className="w-full flex items-end gap-2">
              <div className="flex-1 min-w-0">
                <FiltroData value={range} loading={loading} onChange={setRange} />
              </div>
              <button
                onClick={onRefresh}
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-[0.7rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] text-[color:var(--text-muted)] transition-all duration-[var(--cp-motion-fast,180ms)] hover:border-[color:var(--cp-border-strong,var(--border))] hover:bg-[color:var(--cp-surface,var(--surface))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))] motion-reduce:transition-none"
                title="Atualizar dados"
                aria-label="Atualizar dados"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>

            <div className="w-full flex items-center justify-between gap-2">
              <button
                onClick={() => setFiltrosMinimizados(!filtrosMinimizados)}
                className="relative inline-flex min-h-[44px] items-center justify-center gap-2 rounded-[0.7rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-3 py-2 text-sm font-medium text-[color:var(--text-muted)] transition-all duration-[var(--cp-motion-fast,180ms)] hover:border-[color:var(--cp-border-strong,var(--border))] hover:bg-[color:var(--cp-surface,var(--surface))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))] motion-reduce:transition-none"
                title={filtrosMinimizados ? 'Mostrar filtros avançados' : 'Ocultar filtros avançados'}
                aria-label={filtrosMinimizados ? 'Mostrar filtros avançados' : 'Ocultar filtros avançados'}
                aria-expanded={!filtrosMinimizados}
                aria-controls="contas-pagar-filtros-detalhados"
              >
                {filtrosMinimizados ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                <span>Avançados ({filtrosAvancadosAtivosCount})</span>
              </button>
              {temFiltrosAtivos() && (
                <button
                  onClick={limparTodosFiltros}
                  className="inline-flex min-h-[44px] items-center justify-center rounded-[0.7rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-3 py-2 text-sm text-[color:var(--text-muted)] transition-all duration-[var(--cp-motion-fast,180ms)] hover:border-[color:var(--cp-border-strong,var(--border))] hover:bg-[color:var(--cp-surface,var(--surface))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))] motion-reduce:transition-none"
                  aria-label="Limpar filtros ativos"
                  type="button"
                >
                  <RotateCcw className="mr-1 h-4 w-4" />
                  Limpar
                </button>
              )}
            </div>

            {filtrosAtivosResumo.length > 0 && (
              <div className="w-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <div className="flex min-w-max items-center gap-2 pt-1">
                  {filtrosAtivosResumo.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => removerFiltroAtivo(item.key)}
                      className="inline-flex min-h-[36px] items-center gap-1 rounded-full border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-2.5 py-1 text-xs font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--cp-surface,var(--surface))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))]"
                      aria-label={`Remover filtro ${item.label}`}
                    >
                      <span className="truncate max-w-[200px]">{item.label}</span>
                      <span aria-hidden="true" className="text-[color:var(--text-muted)]">x</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Desktop: busca */}
          <div className="hidden xl:flex flex-1 min-w-0">
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Buscar por fornecedor, descrição, categoria ou ID..."
                className="w-full min-h-[44px] rounded-[0.7rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-4 py-2.5 pr-10 text-sm transition-all duration-[var(--cp-motion-fast,180ms)] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))] focus-visible:border-[color:var(--accent)] hover:border-[color:var(--cp-border-strong,var(--border))]"
                value={buscaLocal}
                onChange={(e) => setBuscaLocal(e.target.value)}
                aria-label="Buscar contas a pagar"
              />
              <Search className="h-4 w-4 text-[color:var(--text-muted)] absolute right-3 top-3" />
            </div>
          </div>

          {/* Desktop: período */}
          <div className="hidden xl:flex items-center gap-2 flex-shrink-0 overflow-visible relative">
            <FiltroData value={range} loading={loading} onChange={setRange} />
          </div>

          {/* Desktop: ações */}
          <div className="hidden xl:flex items-center gap-2 flex-shrink-0">
            {/* Botão para minimizar/maximizar filtros */}
            <button
              onClick={() => setFiltrosMinimizados(!filtrosMinimizados)}
              className="relative inline-flex items-center gap-1 rounded-[0.7rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-3 py-2 text-sm text-[color:var(--text-muted)] transition-all duration-[var(--cp-motion-fast,180ms)] hover:border-[color:var(--cp-border-strong,var(--border))] hover:bg-[color:var(--cp-surface,var(--surface))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))] motion-reduce:transition-none"
              title={filtrosMinimizados ? 'Mostrar filtros' : 'Ocultar filtros'}
              aria-expanded={!filtrosMinimizados}
              aria-controls="contas-pagar-filtros-detalhados"
            >
                          {filtrosMinimizados ? (
                            <>
                              <ChevronDown className="h-4 w-4" />
                              <span className="hidden sm:inline">Mostrar Filtros</span>
                            </>
                          ) : (
                            <>
                              <ChevronUp className="h-4 w-4" />
                              <span className="hidden sm:inline">Ocultar Filtros</span>
                            </>
                          )}
              {/* Badge indicando filtros ativos quando minimizado */}
              {filtrosMinimizados && temFiltrosAtivos() && (
                <span className="absolute -top-1 -right-1 h-5 w-5 bg-[color:var(--accent)] text-white text-xs rounded-full flex items-center justify-center font-semibold">
	                  {filtrosAtivosCount}
                </span>
              )}
            </button>
            {temFiltrosAtivos() && (
              <button
                onClick={limparTodosFiltros}
                className="inline-flex items-center gap-1 rounded-[0.7rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-3 py-2 text-sm text-[color:var(--text-muted)] transition-all duration-[var(--cp-motion-fast,180ms)] hover:border-[color:var(--cp-border-strong,var(--border))] hover:bg-[color:var(--cp-surface,var(--surface))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))] motion-reduce:transition-none"
                title="Limpar todos os filtros"
              >
                <RotateCcw className="h-4 w-4" />
                Limpar
              </button>
            )}
            <button 
              onClick={onRefresh}
              className="inline-flex items-center gap-1 rounded-[0.7rem] border border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] px-3 py-2 text-sm text-[color:var(--text-muted)] transition-all duration-[var(--cp-motion-fast,180ms)] hover:border-[color:var(--cp-border-strong,var(--border))] hover:bg-[color:var(--cp-surface,var(--surface))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))] motion-reduce:transition-none"
              title="Atualizar dados"
            >
              <RefreshCw className="h-4 w-4" />
              Atualizar
            </button>
          </div>
        </div>
      </div>

      {/* Filtros organizados em linha compacta - com animação de colapso */}
      <div 
        id="contas-pagar-filtros-detalhados"
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          filtrosMinimizados ? 'max-h-0 opacity-0' : 'max-h-[2000px] opacity-100'
        }`}
      >
        <div className="p-4 w-full max-w-full">
          <div className="flex flex-col lg:flex-row lg:items-start gap-4 w-full">
          {/* Categorias - sempre visível */}
	          <div className="flex-1 min-w-0 w-full lg:w-auto">
	            {/* Unidade - aparece apenas quando o dataset possui esse campo */}
	            {unidadesUnicas.length > 0 && (
	              <div className="mb-4">
	                <div className="flex items-center gap-2 mb-2">
	                  <Building2 className="h-4 w-4 text-[color:var(--text-muted)]" />
	                  <span className="text-sm font-medium text-[color:var(--text-primary)]">Unidade:</span>
	                </div>
	                <SearchableSelect
	                  options={unidadesUnicas.map((unidade) => ({ id: unidade, label: unidade }))}
	                  value={filtroUnidade ? { id: filtroUnidade, label: filtroUnidade } : { id: '', label: 'Todas as unidades' }}
	                  onChange={(opt) => setFiltroUnidade(opt?.id ? opt.id : null)}
	                  placeholder="Todas as unidades"
	                  searchFields={['label', 'id']}
	                />
	              </div>
	            )}

	            {/* Conta Bancária - adicionado no topo */}
	            <div className="mb-4">
	              <div className="flex items-center gap-2 mb-2">
	                <Building2 className="h-4 w-4 text-[color:var(--text-muted)]" />
                <span className="text-sm font-medium text-[color:var(--text-primary)]">Conta Bancária:</span>
              </div>
              <SearchableSelect
                options={contasBancariasUnicas.map((conta) => ({ id: conta, label: conta }))}
                value={filtroContaBancaria ? { id: filtroContaBancaria, label: filtroContaBancaria } : { id: '', label: 'Todas as contas' }}
                onChange={(opt) => setFiltroContaBancaria(opt?.id ? opt.id : null)}
                placeholder="Todas as contas"
                searchFields={['label', 'id']}
              />
            </div>

            {/* Botão para expandir/colapsar filtros principais em mobile */}
            <div className="lg:hidden mb-3">
              <button
                onClick={() => setFiltrosExpandidos(prev => ({ ...prev, principais: !prev.principais }))}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] rounded-lg hover:bg-[color:var(--surface)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
                aria-expanded={filtrosExpandidos.principais}
                aria-controls="contas-pagar-filtros-principais"
              >
                <span>Filtros Principais ({filtrosPrincipaisAtivosCount})</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${filtrosExpandidos.principais ? 'rotate-180' : ''}`} />
              </button>
            </div>

            <div id="contas-pagar-filtros-principais" className={`${filtrosExpandidos.principais ? 'block' : 'hidden'} lg:block`}>
              <FiltrosCategoria
                contas={contasFonte}
                selectedPlanejamento={filtroPlanejamento}
                setSelectedPlanejamento={setFiltroPlanejamento}
                selectedSubcategoria={filtroSubcategoria}
                setSelectedSubcategoria={setFiltroSubcategoria}
                inline
              />
              
              {/* Tipo de Pagamento - abaixo das categorias */}
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-4 w-4 text-[color:var(--text-muted)]" />
                  <span className="text-sm font-medium text-[color:var(--text-primary)]">Tipo de Pagamento:</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: 'todos', label: 'Todos', color: 'blue' },
                    { id: 'pix', label: 'PIX', color: 'purple' },
                    { id: 'boleto', label: 'Boleto', color: 'cyan' },
                    { id: 'transferência', label: 'Transferência', color: 'green' },
                    { id: 'dinheiro', label: 'Dinheiro', color: 'yellow' },
                    { id: 'cartão', label: 'Cartão', color: 'indigo' },
                    { id: 'cheque', label: 'Cheque', color: 'red' }
                  ].map((tipo) => {
                    const isActive = filtroTipoPagamento === tipo.id;
                    return (
                      <button
                        key={tipo.id}
                        onClick={() => setFiltroTipoPagamento(tipo.id)}
                        className={getColorClasses(tipo.color, isActive)}
                        aria-pressed={isActive}
                      >
                        <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-[color:var(--surface)]' : 'bg-[color:var(--text-muted)]/70'}`}></div>
                        {tipo.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Arquivos e Documentos - colapsável em mobile */}
          <div className="flex-1 min-w-0 w-full lg:w-auto">
            {/* Botão para expandir/colapsar filtros de arquivos em mobile */}
            <div className="lg:hidden mb-3">
              <button
                onClick={() => setFiltrosExpandidos(prev => ({ ...prev, arquivos: !prev.arquivos }))}
                className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-[color:var(--text-primary)] bg-[color:var(--surface-muted)] rounded-lg hover:bg-[color:var(--surface)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)]"
                aria-expanded={filtrosExpandidos.arquivos}
                aria-controls="contas-pagar-filtros-arquivos"
              >
                <span>Filtros de Arquivos ({filtrosArquivosAtivosCount})</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${filtrosExpandidos.arquivos ? 'rotate-180' : ''}`} />
              </button>
            </div>

            <div id="contas-pagar-filtros-arquivos" className={`${filtrosExpandidos.arquivos ? 'block' : 'hidden'} lg:block space-y-3`}>
              {/* Status dos Arquivos */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-[color:var(--text-muted)]" />
                  <span className="text-sm font-medium text-[color:var(--text-primary)]">Arquivos:</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { id: 'todas', label: 'Todas', color: 'blue' },
                    { id: 'com_arquivos', label: 'Com arquivos', color: 'green' },
                    { id: 'sem_arquivos', label: 'Sem arquivos', color: 'orange' }
                  ].map(({ id, label, color }) => {
                    const isActive = filtroArquivosAtual === id;
                    return (
                      <button
                        key={id}
                        onClick={() => setFiltroArquivosAtual(id)}
                        className={getColorClasses(color, isActive)}
                        aria-pressed={isActive}
                      >
                        <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-[color:var(--surface)]' : 'bg-[color:var(--text-muted)]/70'}`}></div>
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tipos de Documento - só aparece se não for "sem arquivos" */}
              {filtroArquivosAtual !== 'sem_arquivos' && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Filter className="h-4 w-4 text-[color:var(--text-muted)]" />
                    <span className="text-sm font-medium text-[color:var(--text-primary)]">Tipo:</span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {tiposDocumento.map((tipo) => {
                      const isActive = filtroTipoDocumento === tipo.id;
                      return (
                        <button
                          key={tipo.id}
                          onClick={() => setFiltroTipoDocumento(tipo.id)}
                          className={getColorClasses(tipo.color, isActive)}
                          aria-pressed={isActive}
                        >
                          <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-[color:var(--surface)]' : 'bg-[color:var(--text-muted)]/70'}`}></div>
                          {tipo.label}
                        </button>
                      );
                    })}
                  </div>

                  {/* Toggle de inclusão/exclusão */}
                  {filtroTipoDocumento !== 'todos' && (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-[color:var(--text-muted)]">
                          {negacaoFiltro ? 'Excluir' : 'Incluir'} este tipo
                        </span>
                        <button
                          type="button"
                          onClick={() => setNegacaoFiltro(!negacaoFiltro)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)] ${
                            negacaoFiltro
                              ? 'bg-[color:var(--status-danger-bg,var(--surface-muted))] border border-[color:var(--status-danger-border,var(--border))]'
                              : 'bg-[color:var(--status-good-bg,var(--surface-muted))] border border-[color:var(--status-good-border,var(--border))]'
                          }`}
                          role="switch"
                          aria-checked={negacaoFiltro}
                          aria-label={negacaoFiltro ? 'Excluir tipo selecionado' : 'Incluir tipo selecionado'}
                        >
                          <span
                            className={`inline-block h-3 w-3 transform rounded-full bg-[color:var(--surface)] transition-transform ${
                              negacaoFiltro ? 'translate-x-5' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Busca por documento */}
                  <div className="mt-2">
                    <input
                      type="text"
                      placeholder="Buscar pelo nome do anexo..."
                      value={buscaDocumentoLocal}
                      onChange={(e) => setBuscaDocumentoLocal(e.target.value)}
                      className="w-full min-h-[44px] px-3 py-2 text-sm border border-[color:var(--border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)] bg-[color:var(--surface-muted)] focus:bg-[color:var(--surface)] transition-all"
                      aria-label="Buscar pelo nome do anexo"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FiltrosOtimizados;
